import React, { useState } from 'react';
import { X, Settings, Trash2, Package, Wifi, Router, Server, Camera, Cable, Zap, HardDrive, Eye, ChevronDown, ChevronRight, Layout, Radio, Ruler, FileText, FileCheck, BarChart3, Sparkles, Pin, Minus, Plus, Plug } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useProjectStore } from '../../store/projectStore';
import { useAuthStore } from '../../store/authStore';
import type { AppUIFont } from '../../utils/appUIFont';
import { useSiteStore } from '../../store/siteStore';
import { equipmentDatabase } from '../../data/equipment';
import { formatCurrency } from '../../utils/currencyUtils';
import { PSU_CATALOG } from '../../data/deviceModels';
import { isLogicExcluded, resolveDeviceModel, collectLayoutPorts, compareLayoutToModel, rebuildModelFromLayout } from '../../engine/networkLogic';
import { POE_STANDARD_LABEL, POE_STANDARDS, portLogicKey } from '../../types/deviceModel';
import { setNodesLogicExcluded } from '../../utils/logicParticipation';
import { useLogicStore } from '../../store/logicStore';
import { rackNodeId } from '../../engine/networkLogic/resolveModel';

const RightSidebar: React.FC = () => {
  const [showEquipmentChange, setShowEquipmentChange] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    general: false,
    designer: false,
    betaFeatures: false,
    sites: false,
    floorplan: false,
    reports: false,
    proposal: false,
    analytics: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const { profile, user } = useAuthStore();
  const displayName = profile?.displayName ?? user?.email?.split('@')[0] ?? 'User';
  const displayInitial = displayName.charAt(0).toUpperCase();
  const accountEmail = user?.email ?? '';

  const {
    selectedRack, 
    selectedEquipment, 
    selectedRackEquipment,
    selectedCable,
    currentProject,
    isRackConfigOpen,
    isEquipmentConfigOpen,
    isCableConfigOpen,
    isSettingsConfigOpen,
    isSettingsPinned,
    setRackConfigOpen,
    setEquipmentConfigOpen,
    setCableConfigOpen,
    setSelectedEquipmentForPortView,
    setPortVisualizationOpen,
    setSettingsConfigOpen,
    setIsSettingsPinned,
    updateEquipmentConfig,
    setEquipmentLabel,
    updateRackEquipmentConfig,
    updateCableRun,
    deleteCableRun,
    removeRack,
    showAPRange,
    setShowAPRange,
    autosaveSettings,
    updateAutosaveSettings,
    featureFlags,
    updateFeatureFlags,
    resetFeatureFlags,
    lastSaved,
    isAutoSaving,
    snapToGrid,
    snapToGridTarget,
    gridSize,
    showGrid,
    showLegend,
    setSnapToGrid,
    setSnapToGridTarget,
    setGridSize,
    setShowGrid,
    setShowLegend,
    showMinimap,
    setShowMinimap,
    alwaysShowExpandedEquipment,
    setAlwaysShowExpandedEquipment,
    portsDisplayMode,
    setPortsDisplayMode,
    canvasBackground,
    setCanvasBackground,
    badgeAnchor,
    setBadgeAnchor,
    panelVisualization,
    setPanelVisualization,
    wallDragMode,
    setWallDragMode,
    currency,
    setCurrency,
    appTheme,
    setAppTheme,
    appUIFont,
    setAppUIFont,
    showDesignInfoOverlay,
    setShowDesignInfoOverlay,
    showManufacturerInEquipmentList,
    setShowManufacturerInEquipmentList,
    expandBadgesOnHover,
    setExpandBadgesOnHover,
    colourizePorts,
    setColourizePorts,
    equipmentPanelAdvancedDefault,
    setEquipmentPanelAdvancedDefault,
    floorPlanUnits,
    getFloorPlan,
    setFloorPlanUnits,
    clearFloorPlanReferenceImages,
    canvasMode,
    setCanvasMode,
    canvasCollabMode,
    setCanvasCollabMode,
  } = useProjectStore(
    useShallow((s) => ({
      selectedRack: s.selectedRack,
      selectedEquipment: s.selectedEquipment,
      selectedRackEquipment: s.selectedRackEquipment,
      selectedCable: s.selectedCable,
      currentProject: s.currentProject,
      isRackConfigOpen: s.isRackConfigOpen,
      isEquipmentConfigOpen: s.isEquipmentConfigOpen,
      isCableConfigOpen: s.isCableConfigOpen,
      isSettingsConfigOpen: s.isSettingsConfigOpen,
      isSettingsPinned: s.isSettingsPinned,
      setRackConfigOpen: s.setRackConfigOpen,
      setEquipmentConfigOpen: s.setEquipmentConfigOpen,
      setCableConfigOpen: s.setCableConfigOpen,
      setSelectedEquipmentForPortView: s.setSelectedEquipmentForPortView,
      setPortVisualizationOpen: s.setPortVisualizationOpen,
      setSettingsConfigOpen: s.setSettingsConfigOpen,
      setIsSettingsPinned: s.setIsSettingsPinned,
      updateEquipmentConfig: s.updateEquipmentConfig,
      setEquipmentLabel: s.setEquipmentLabel,
      updateRackEquipmentConfig: s.updateRackEquipmentConfig,
      updateCableRun: s.updateCableRun,
      deleteCableRun: s.deleteCableRun,
      removeRack: s.removeRack,
      showAPRange: s.showAPRange,
      setShowAPRange: s.setShowAPRange,
      autosaveSettings: s.autosaveSettings,
      updateAutosaveSettings: s.updateAutosaveSettings,
      featureFlags: s.featureFlags,
      updateFeatureFlags: s.updateFeatureFlags,
      resetFeatureFlags: s.resetFeatureFlags,
      lastSaved: s.lastSaved,
      isAutoSaving: s.isAutoSaving,
      snapToGrid: s.snapToGrid,
      snapToGridTarget: s.snapToGridTarget,
      gridSize: s.gridSize,
      showGrid: s.showGrid,
      showLegend: s.showLegend,
      setSnapToGrid: s.setSnapToGrid,
      setSnapToGridTarget: s.setSnapToGridTarget,
      setGridSize: s.setGridSize,
      setShowGrid: s.setShowGrid,
      setShowLegend: s.setShowLegend,
      showMinimap: s.showMinimap,
      setShowMinimap: s.setShowMinimap,
      alwaysShowExpandedEquipment: s.alwaysShowExpandedEquipment,
      setAlwaysShowExpandedEquipment: s.setAlwaysShowExpandedEquipment,
      portsDisplayMode: s.portsDisplayMode,
      setPortsDisplayMode: s.setPortsDisplayMode,
      canvasBackground: s.canvasBackground,
      setCanvasBackground: s.setCanvasBackground,
      badgeAnchor: s.badgeAnchor,
      setBadgeAnchor: s.setBadgeAnchor,
      panelVisualization: s.panelVisualization,
      setPanelVisualization: s.setPanelVisualization,
      wallDragMode: s.wallDragMode,
      setWallDragMode: s.setWallDragMode,
      currency: s.currency,
      setCurrency: s.setCurrency,
      appTheme: s.appTheme,
      setAppTheme: s.setAppTheme,
      appUIFont: s.appUIFont,
      setAppUIFont: s.setAppUIFont,
      showDesignInfoOverlay: s.showDesignInfoOverlay,
      setShowDesignInfoOverlay: s.setShowDesignInfoOverlay,
      showManufacturerInEquipmentList: s.showManufacturerInEquipmentList,
      setShowManufacturerInEquipmentList: s.setShowManufacturerInEquipmentList,
      expandBadgesOnHover: s.expandBadgesOnHover,
      setExpandBadgesOnHover: s.setExpandBadgesOnHover,
      colourizePorts: s.colourizePorts,
      setColourizePorts: s.setColourizePorts,
      equipmentPanelAdvancedDefault: s.equipmentPanelAdvancedDefault,
      setEquipmentPanelAdvancedDefault: s.setEquipmentPanelAdvancedDefault,
      floorPlanUnits: s.floorPlanUnits,
      getFloorPlan: s.getFloorPlan,
      setFloorPlanUnits: s.setFloorPlanUnits,
      clearFloorPlanReferenceImages: s.clearFloorPlanReferenceImages,
      canvasMode: s.canvasMode,
      setCanvasMode: s.setCanvasMode,
      canvasCollabMode: s.canvasCollabMode,
      setCanvasCollabMode: s.setCanvasCollabMode,
    }))
  );
  const { showTowerLabels, setShowTowerLabels } = useSiteStore();
  const isDarkTheme = appTheme === 'dark';
  const logicSelectionIds = useLogicStore((s) => s.selectionIds);
  const floorPlan = getFloorPlan();
  const referenceImages = floorPlan?.referenceImages ?? [];

  const isOpen = isRackConfigOpen || isEquipmentConfigOpen || isCableConfigOpen || isSettingsConfigOpen || (selectedRackEquipment && isEquipmentConfigOpen);

  const handleConfigChange = (key: string, value: string) => {
    if (selectedEquipment) {
      updateEquipmentConfig(selectedEquipment.id, { [key]: value });
    }
  };

  const handleRackEquipmentConfigChange = (key: string, value: string) => {
    if (selectedRackEquipment) {
      updateRackEquipmentConfig(selectedRackEquipment.rackId, selectedRackEquipment.index, { [key]: value });
    }
  };


  const handleCableConfigChange = (key: string, value: string) => {
    if (selectedCable) {
      updateCableRun(selectedCable.id, { [key]: value });
    }
  };

  const handleDeleteCable = () => {
    if (selectedCable) {
      deleteCableRun(selectedCable.id);
      setCableConfigOpen(false);
    }
  };

  const handleDeleteRack = () => {
    if (selectedRack) {
      if (window.confirm(`Are you sure you want to delete ${selectedRack.name}? This will also remove all equipment and cables connected to this rack.`)) {
        removeRack(selectedRack.id);
        setRackConfigOpen(false);
      }
    }
  };

  const handleEquipmentChange = (newEquipment: any) => {
    if (selectedEquipment) {
      // Update the equipment while preserving position and config
      const state = useProjectStore.getState();
      if (!state.currentProject) return;

      const updatedEquipment = state.currentProject.equipment.map(eq =>
        eq.id === selectedEquipment.id 
          ? { 
              ...eq, 
              equipment: newEquipment,
              // Reset config as it might not be compatible
              config: {}
            } 
          : eq
      );

      useProjectStore.setState({
        currentProject: {
          ...state.currentProject,
          equipment: updatedEquipment,
          updatedAt: new Date()
        },
        selectedEquipment: {
          ...selectedEquipment,
          equipment: newEquipment,
          config: {}
        }
      });
    }
    setShowEquipmentChange(false);
  };

  const handleDeleteEquipment = () => {
    if (selectedEquipment) {
      const state = useProjectStore.getState();
      if (!state.currentProject) return;

      const updatedEquipment = state.currentProject.equipment.filter(
        eq => eq.id !== selectedEquipment.id
      );

      useProjectStore.setState({
        currentProject: {
          ...state.currentProject,
          equipment: updatedEquipment,
          updatedAt: new Date()
        },
        selectedEquipment: null,
        isEquipmentConfigOpen: false
      });
    }
  };

  const getEquipmentIcon = (type: string) => {
    switch (type) {
      case 'ap': return <Wifi size={16} />;
      case 'switch': return <Router size={16} />;
      case 'router': return <Router size={16} />;
      case 'server': return <Server size={16} />;
      case 'camera': return <Camera size={16} />;
      case 'cable-run': return <Cable size={16} />;
      case 'patch-panel': return <Package size={16} />;
      case 'poe-injector': return <Zap size={16} />;
      case 'psu': return <Zap size={16} />;
      case 'ups': return <HardDrive size={16} />;
      case 'wall-outlet': return <Plug size={16} />;
      case 'radio-dish':
      case 'sector-antenna':
        return <Radio size={16} />;
      default: return <Package size={16} />;
    }
  };

  return (
    <div className={`
      fixed top-0 right-0 h-full w-96 text-white border-l z-[60] ${isDarkTheme ? 'settings-dark-neutral' : ''}
      transform transition-transform duration-300 ease-in-out
      ${isDarkTheme ? 'bg-[#171a1f] border-[#343a43]' : 'bg-slate-900 border-slate-700'}
      ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      flex flex-col
    `}>
      {/* Fixed Header */}
      <div className={`flex-shrink-0 p-6 pb-4 border-b ${isDarkTheme ? 'border-[#343a43]' : 'border-slate-700'}`}>
        <div className="flex items-center justify-between">
          <h2 className={`text-xl font-semibold ${isDarkTheme ? 'text-gray-200' : 'text-blue-400'}`}>
            {isRackConfigOpen && 'Rack Configuration'}
            {isEquipmentConfigOpen && 'Equipment Configuration'}
            {isCableConfigOpen && 'Cable Configuration'}
            {isSettingsConfigOpen && 'NetBuilder Settings'}
          </h2>
          <div className="flex items-center gap-1">
            {isSettingsConfigOpen && (
              <button
                onClick={() => setIsSettingsPinned(!isSettingsPinned)}
                className={`p-2 rounded-md transition-colors ${
                  isSettingsPinned 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'hover:bg-slate-800 text-gray-300'
                }`}
                title={isSettingsPinned ? 'Unpin settings' : 'Pin settings open'}
              >
                <Pin size={18} className={isSettingsPinned ? 'fill-current' : ''} />
              </button>
            )}
            <button
              onClick={() => {
                if (isSettingsConfigOpen && isSettingsPinned) {
                  // Don't close if pinned
                  return;
                }
                setRackConfigOpen(false);
                setEquipmentConfigOpen(false);
                setCableConfigOpen(false);
                setSettingsConfigOpen(false);
                setShowAccountSettings(false);
                // Reset all sections to collapsed when Settings is closed
                setExpandedSections({
                  general: false,
                  designer: false,
                  betaFeatures: false,
                  sites: false,
                  floorplan: false,
                  reports: false,
                  proposal: false,
                  analytics: false,
                });
              }}
              className="p-2 hover:bg-slate-800 rounded-md transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-6 pt-4" style={{ scrollbarGutter: 'stable' }}>

      {/* Rack Configuration */}
      {isRackConfigOpen && selectedRack && (
        <div className="space-y-6">
          <div className="bg-slate-800 rounded-lg p-4">
            <h3 className="font-medium mb-3 flex items-center">
              <Settings size={16} className="mr-2" />
              Rack Details
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  defaultValue={selectedRack.name}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Size</label>
                <select className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500">
                  <option value={selectedRack.size}>{selectedRack.size}U</option>
                </select>
              </div>
            </div>
          </div>

          {/* Rack Equipment */}
          <div className="bg-slate-800 rounded-lg p-4">
            <h3 className="font-medium mb-3">Equipment ({selectedRack.equipment.length})</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
              {selectedRack.equipment.map((rackEq, index) => (
                <div key={index} className="bg-slate-700 rounded-md p-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{rackEq.equipment.name}</div>
                    <div className="text-xs text-gray-400">Unit {rackEq.startUnit}</div>
                  </div>
                  <button className="p-1 hover:bg-slate-600 rounded">
                    <Trash2 size={14} className="text-red-400" />
                  </button>
                </div>
              ))}
              {selectedRack.equipment.length === 0 && (
                <div className="text-center text-gray-400 py-4">
                  No equipment installed
                </div>
              )}
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-slate-800 rounded-lg p-4 border-l-4 border-red-500">
            <h3 className="font-medium mb-3 flex items-center text-red-400">
              <Trash2 size={16} className="mr-2" />
              Danger Zone
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Permanently delete this rack and all its equipment. This action cannot be undone.
            </p>
            <button
              onClick={handleDeleteRack}
              className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center"
            >
              <Trash2 size={16} className="mr-2" />
              Delete Rack
            </button>
          </div>
        </div>
      )}

      {/* Equipment Configuration */}
      {isEquipmentConfigOpen && selectedEquipment && (
        <div className="space-y-6">
          {/* Equipment Header */}
          <div className="mb-6">
            {/* Top text block */}
            <div className="min-w-0 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-semibold text-base leading-5 break-words whitespace-normal">
                  {selectedEquipment.equipment.name}
                </span>
                {!!selectedEquipment.config?.label && (
                  <span className="text-sm text-gray-300 truncate inline-block">• {selectedEquipment.config.label}</span>
                )}
              </div>
              <p className="text-sm text-gray-400 capitalize">
                {selectedEquipment.equipment.manufacturer} • {selectedEquipment.equipment.type}
              </p>
              <div className="text-right mt-1">
                <button
                  onClick={() => {
                    const current = selectedEquipment.config?.label || '';
                    const next = window.prompt('Set label for this equipment', current) || '';
                    const trimmedLabel = next.trim();
                    
                    if (trimmedLabel === '') {
                      useProjectStore.getState().setEquipmentLabel(selectedEquipment.id, '');
                      return;
                    }
                    
                    // Check for duplicate equipment labels (case-insensitive)
                    const currentProject = useProjectStore.getState().currentProject;
                    const duplicateEquipment = currentProject?.equipment.find(eq => 
                      eq.id !== selectedEquipment.id && 
                      eq.config?.label?.toLowerCase().trim() === trimmedLabel.toLowerCase().trim()
                    );
                    
                    if (duplicateEquipment) {
                      alert(`An equipment with the label "${trimmedLabel}" already exists. Please choose a different label.`);
                      return;
                    }
                    
                    useProjectStore.getState().setEquipmentLabel(selectedEquipment.id, trimmedLabel);
                  }}
                  className="px-2 py-0.5 text-sm text-blue-300 hover:text-blue-200 rounded"
                  title="Add Label"
                >
                  + Add Label
                </button>
              </div>
            </div>
            {/* Bottom row: icon + actions */}
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white">
                {getEquipmentIcon(selectedEquipment.equipment.type)}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedEquipmentForPortView(selectedEquipment);
                    setPortVisualizationOpen(true);
                  }}
                  className="p-2 hover:bg-blue-600 rounded-md transition-colors text-blue-400"
                  title="View Ports"
                >
                  <Eye size={16} />
                </button>
                <button
                  onClick={() => setShowEquipmentChange(true)}
                  className="p-2 hover:bg-slate-700 rounded-md transition-colors"
                  title="Change Equipment"
                >
                  <Package size={16} />
                </button>
                <button
                  onClick={handleDeleteEquipment}
                  className="p-2 hover:bg-red-600 rounded-md transition-colors text-red-400"
                  title="Delete Equipment"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>

          {selectedEquipment && (
            <div className="bg-slate-800 rounded-lg p-4">
              <h3 className="font-medium mb-3">Logic</h3>
              {logicSelectionIds.length > 1 && (
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    className="flex-1 text-xs py-1.5 rounded bg-slate-700 hover:bg-slate-600"
                    onClick={() => setNodesLogicExcluded(logicSelectionIds, false)}
                  >
                    Include {logicSelectionIds.length} in Logic
                  </button>
                  <button
                    type="button"
                    className="flex-1 text-xs py-1.5 rounded bg-slate-700 hover:bg-slate-600"
                    onClick={() => setNodesLogicExcluded(logicSelectionIds, true)}
                  >
                    Exclude selected
                  </button>
                </div>
              )}
              <label className="flex items-center justify-between text-sm mb-3">
                <span>Include in Logic</span>
                <input
                  type="checkbox"
                  checked={!isLogicExcluded(selectedEquipment.config, selectedEquipment.equipment.type)}
                  onChange={(e) => {
                    setNodesLogicExcluded([selectedEquipment.id], !e.target.checked);
                  }}
                />
              </label>
              <label className="block text-sm mb-3">
                <span className="text-gray-400 text-xs">Assigned PSU</span>
                <select
                  className="w-full mt-1 bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-sm"
                  value={selectedEquipment.config?.assignedPsuId || ''}
                  onChange={(e) =>
                    useProjectStore.getState().updateEquipmentConfig(
                      selectedEquipment.id,
                      { assignedPsuId: e.target.value || undefined },
                      { persistToLibrary: false }
                    )
                  }
                >
                  <option value="">None (PoE path or cabled PSU)</option>
                  {PSU_CATALOG.map((p) => (
                    <option key={p.equipmentId} value={p.equipmentId}>
                      {p.equipmentId} ({p.psuOutput?.voltage}V {p.psuOutput?.watts}W)
                    </option>
                  ))}
                </select>
              </label>
              {(() => {
                const { model } = resolveDeviceModel(selectedEquipment.equipment.id, selectedEquipment.equipment.type, selectedEquipment.config);
                const ports = collectLayoutPorts(selectedEquipment.config?.layout || model.layout);
                const poeOut = ports.filter((p) => p.poeOut);
                if (!poeOut.length) return null;
                return (
                  <div className="space-y-2">
                    <div className="text-xs text-gray-400">PoE port mode</div>
                    {poeOut.slice(0, 12).map((p) => {
                      const key = portLogicKey(p.face, p.number, String(p.kind));
                      const cur = selectedEquipment.config?.portLogic?.[key] || selectedEquipment.config?.portLogic?.[portLogicKey(p.face, p.number)];
                      return (
                        <div key={key} className="flex items-center gap-2 text-xs">
                          <span className="w-20 truncate">{p.label || `${p.kind}${p.number}`}</span>
                          <select
                            className="flex-1 bg-slate-700 rounded px-1 py-0.5"
                            value={cur?.poeMode === 'forced' ? `forced:${cur.forcedStandard}` : cur?.poeMode || 'auto'}
                            onChange={(e) => {
                              const v = e.target.value;
                              const next = { ...(selectedEquipment.config?.portLogic || {}) };
                              if (v === 'auto') next[key] = { poeMode: 'auto' };
                              else if (v === 'off') next[key] = { poeMode: 'off' };
                              else next[key] = { poeMode: 'forced', forcedStandard: v.replace('forced:', '') as any };
                              useProjectStore.getState().updateEquipmentConfig(selectedEquipment.id, { portLogic: next }, { persistToLibrary: false });
                            }}
                          >
                            <option value="auto">Auto</option>
                            <option value="off">Off</option>
                            {POE_STANDARDS.map((s) => (
                              <option key={s} value={`forced:${s}`}>Forced {POE_STANDARD_LABEL[s]}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {(() => {
                const { model } = resolveDeviceModel(selectedEquipment.equipment.id, selectedEquipment.equipment.type, selectedEquipment.config);
                const cmp = selectedEquipment.config?.deviceModel
                  ? compareLayoutToModel(selectedEquipment.config.deviceModel, selectedEquipment.config)
                  : { ok: true };
                if (cmp.ok) return null;
                return (
                  <button
                    type="button"
                    className="mt-2 w-full text-xs py-1.5 rounded bg-amber-800 hover:bg-amber-700"
                    onClick={() => {
                      const rebuilt = rebuildModelFromLayout(model, selectedEquipment.config);
                      useProjectStore.getState().updateEquipmentConfig(
                        selectedEquipment.id,
                        { deviceModel: rebuilt, layout: rebuilt.layout },
                        { persistToLibrary: false }
                      );
                    }}
                  >
                    Rebuild logic from layout
                  </button>
                );
              })()}
            </div>
          )}

          {/* Equipment Change Modal */}
          {showEquipmentChange && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
              <div className="bg-slate-800 rounded-lg shadow-xl w-96 max-h-96 flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-slate-700">
                  <h3 className="font-semibold">Change Equipment</h3>
                  <button
                    onClick={() => setShowEquipmentChange(false)}
                    className="p-1 hover:bg-slate-700 rounded"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {equipmentDatabase.map(equipment => (
                    <div
                      key={equipment.id}
                      onClick={() => handleEquipmentChange(equipment)}
                      className="flex items-center space-x-3 p-3 hover:bg-slate-700 rounded-md cursor-pointer transition-colors"
                    >
                      <div className="text-blue-400">
                        {getEquipmentIcon(equipment.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{equipment.name}</div>
                        <div className="text-xs text-gray-400">{equipment.manufacturer}</div>
                      </div>
                      {equipment.price && (
                        <div className="text-xs text-green-400">${equipment.price}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}



          {/* Label + Configuration */}
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium">Configuration</h3>
              <div className="flex items-center space-x-2">
                {selectedEquipment.config?.label && (
                  <span className="text-xs text-gray-300">Label: {selectedEquipment.config.label}</span>
                )}
              </div>
            </div>
            <div className="space-y-3">
              {/* Show Port Panel Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium">Show Port Panel</label>
                  <p className="text-xs text-gray-400">Display the ports panel under this equipment</p>
                </div>
                <button
                  onClick={() => updateEquipmentConfig(selectedEquipment.id, { showPortPanel: !(selectedEquipment.config?.showPortPanel !== false) ? false : true ? false : true })}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    (selectedEquipment.config?.showPortPanel !== false) ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      (selectedEquipment.config?.showPortPanel !== false) ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              {selectedEquipment.equipment.type === 'ap' && (
                <>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">SSID</label>
                    <input
                      type="text"
                      value={selectedEquipment.config?.ssid || ''}
                      onChange={(e) => handleConfigChange('ssid', e.target.value)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                      placeholder="Enter SSID"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Channel</label>
                    <select 
                      value={selectedEquipment.config?.channel || ''}
                      onChange={(e) => handleConfigChange('channel', e.target.value)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    >
                      <option value="">Auto</option>
                      <option value="1">Channel 1</option>
                      <option value="6">Channel 6</option>
                      <option value="11">Channel 11</option>
                    </select>
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">IP</label>
                <input
                  type="text"
                  value={selectedEquipment.config?.ip || ''}
                  onChange={(e) => handleConfigChange('ip', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  placeholder="192.168.1.100"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Subnet</label>
                <input
                  type="text"
                  value={selectedEquipment.config?.subnet || '255.255.255.0'}
                  onChange={(e) => handleConfigChange('subnet', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  placeholder="255.255.255.0"
                />
              </div>
              
              {(selectedEquipment.equipment.type === 'switch' || selectedEquipment.equipment.type === 'ap') && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">VLAN</label>
                  <input
                    type="text"
                    value={selectedEquipment.config?.vlan || ''}
                    onChange={(e) => handleConfigChange('vlan', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    placeholder="100"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-slate-800 rounded-lg p-4">
            <h3 className="font-medium mb-3">Specifications</h3>
            <div className="space-y-2 text-sm">
              {Object.entries(selectedEquipment.equipment.specifications || {}).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="text-gray-400 capitalize">{key}:</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
              {selectedEquipment.equipment.price && (
                <div className="flex justify-between pt-2 border-t border-slate-700">
                  <span className="text-gray-400">Price:</span>
                  <span className="font-medium text-green-400">${selectedEquipment.equipment.price}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rack Equipment Configuration */}
      {isEquipmentConfigOpen && selectedRackEquipment && (
        <div className="space-y-6">
          {/* Rack Equipment Header */}
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center text-white">
              {getEquipmentIcon(selectedRackEquipment.equipment.type)}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">{selectedRackEquipment.equipment.name}</h3>
              <p className="text-sm text-gray-400 capitalize">
                {selectedRackEquipment.equipment.manufacturer} • {selectedRackEquipment.equipment.type} • Rack Equipment
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setEquipmentConfigOpen(false);
                  useProjectStore.setState({ selectedRackEquipment: null });
                }}
                className="p-2 hover:bg-slate-700 rounded-md transition-colors text-gray-400"
                title="Close Configuration"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Rack Equipment Configuration */}
          <div className="bg-slate-800 rounded-lg p-4">
            <h3 className="font-medium mb-3">Logic</h3>
            {(() => {
              const design = useProjectStore.getState().getCurrentDesign();
              const inst = design?.racks
                ?.find((r) => r.id === selectedRackEquipment.rackId)
                ?.equipment[selectedRackEquipment.index];
              const cfg = inst?.config;
              const nodeId = inst
                ? rackNodeId(selectedRackEquipment.rackId, inst, selectedRackEquipment.index)
                : '';
              const excluded = isLogicExcluded(cfg, selectedRackEquipment.equipment.type);
              return (
                <>
                  <label className="flex items-center justify-between text-sm mb-3">
                    <span>Include in Logic</span>
                    <input
                      type="checkbox"
                      checked={!excluded}
                      onChange={(e) => setNodesLogicExcluded([nodeId], !e.target.checked)}
                    />
                  </label>
                  <label className="block text-sm mb-1">
                    <span className="text-gray-400 text-xs">Assigned PSU</span>
                    <select
                      className="w-full mt-1 bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-sm"
                      value={cfg?.assignedPsuId || ''}
                      onChange={(e) =>
                        updateRackEquipmentConfig(selectedRackEquipment.rackId, selectedRackEquipment.index, {
                          assignedPsuId: e.target.value || undefined,
                        })
                      }
                    >
                      <option value="">None (PoE path or cabled PSU)</option>
                      {PSU_CATALOG.map((p) => (
                        <option key={p.equipmentId} value={p.equipmentId}>
                          {p.equipmentId} ({p.psuOutput?.voltage}V {p.psuOutput?.watts}W)
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              );
            })()}
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <h3 className="font-medium mb-3">Configuration</h3>
            <div className="space-y-3">
              {selectedRackEquipment.equipment.type === 'access-point' && (
                <>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">SSID</label>
                    <input
                      type="text"
                      value={selectedRackEquipment.equipment.config?.ssid || ''}
                      onChange={(e) => handleRackEquipmentConfigChange('ssid', e.target.value)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                      placeholder="Network Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Channel</label>
                    <input
                      type="text"
                      value={selectedRackEquipment.equipment.config?.channel || ''}
                      onChange={(e) => handleRackEquipmentConfigChange('channel', e.target.value)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                      placeholder="1, 6, 11, auto"
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">IP Address</label>
                <input
                  type="text"
                  value={selectedRackEquipment.equipment.config?.ip || ''}
                  onChange={(e) => handleRackEquipmentConfigChange('ip', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  placeholder="192.168.1.100"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Subnet</label>
                <input
                  type="text"
                  value={selectedRackEquipment.equipment.config?.subnet || ''}
                  onChange={(e) => handleRackEquipmentConfigChange('subnet', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  placeholder="255.255.255.0"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">VLAN</label>
                <input
                  type="text"
                  value={selectedRackEquipment.equipment.config?.vlan || ''}
                  onChange={(e) => handleRackEquipmentConfigChange('vlan', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  placeholder="100"
                />
              </div>
            </div>
          </div>

          {/* Rack Equipment Details */}
          <div className="bg-slate-800 rounded-lg p-4">
            <h3 className="font-medium mb-3">Equipment Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Model:</span>
                <span className="font-medium">{selectedRackEquipment.equipment.model}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Manufacturer:</span>
                <span className="font-medium">{selectedRackEquipment.equipment.manufacturer}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Rack Units:</span>
                <span className="font-medium">{selectedRackEquipment.equipment.units || 1}U</span>
              </div>
              {selectedRackEquipment.equipment.specifications?.power && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Power:</span>
                  <span className="font-medium">{selectedRackEquipment.equipment.specifications.power}</span>
                </div>
              )}
              {selectedRackEquipment.equipment.specifications?.ports && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Ports:</span>
                  <span className="font-medium">{selectedRackEquipment.equipment.specifications.ports}</span>
                </div>
              )}
              {selectedRackEquipment.equipment.price && (
                <div className="flex justify-between pt-2 border-t border-slate-700">
                  <span className="text-gray-400">Price:</span>
                  <span className="font-medium text-green-400">{formatCurrency(selectedRackEquipment.equipment.price, currency)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cable Configuration */}
      {isCableConfigOpen && selectedCable && (
        <div className="space-y-6">
          {/* Cable Header */}
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center text-white">
              <Cable size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">{selectedCable.label || `${selectedCable.type} Cable`}</h3>
              <p className="text-sm text-gray-400 capitalize">
                {selectedCable.type} Connection
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleDeleteCable}
                className="p-2 hover:bg-red-600 rounded-md transition-colors text-red-400"
                title="Delete Cable"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          {/* Cable Configuration */}
          <div className="bg-slate-800 rounded-lg p-4">
            <h3 className="font-medium mb-3">Configuration</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Cable Name</label>
                <input
                  type="text"
                  value={selectedCable.label || ''}
                  onChange={(e) => handleCableConfigChange('label', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  placeholder={`${selectedCable.type} Cable`}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Cable Type</label>
                <select 
                  value={selectedCable.type}
                  onChange={(e) => handleCableConfigChange('type', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="ethernet">Ethernet</option>
                  <option value="fiber">Fiber Optic</option>
                  <option value="power">Power Cable</option>
                </select>
              </div>

              {selectedCable.type === 'ethernet' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Ethernet Type</label>
                  <select 
                    value={selectedCable.subtype || ''}
                    onChange={(e) => handleCableConfigChange('subtype', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select Ethernet Type</option>
                    <option value="Cat5e CCA Indoor">Cat5e CCA Indoor</option>
                    <option value="Cat5e CCA Outdoor">Cat5e CCA Outdoor</option>
                    <option value="Cat5e Bare Copper Indoor">Cat5e Bare Copper Indoor</option>
                    <option value="Cat5e Bare Copper Outdoor">Cat5e Bare Copper Outdoor</option>
                    <option value="Cat6 CCA Indoor">Cat6 CCA Indoor</option>
                    <option value="Cat6 CCA Outdoor">Cat6 CCA Outdoor</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm text-gray-400 mb-1">Length (optional)</label>
                <input
                  type="text"
                  value={selectedCable.length || ''}
                  onChange={(e) => handleCableConfigChange('length', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  placeholder="50ft"
                />
              </div>
            </div>
          </div>

          {/* Connection Details */}
          <div className="bg-slate-800 rounded-lg p-4">
            <h3 className="font-medium mb-3">Connection</h3>
            <div className="space-y-2 text-sm">
              {(() => {
                const fromEquipment = currentProject?.equipment.find(eq => eq.id === selectedCable.from);
                const toEquipment = currentProject?.equipment.find(eq => eq.id === selectedCable.to);
                return (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">From:</span>
                      <span className="font-medium">{fromEquipment?.equipment.name || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">To:</span>
                      <span className="font-medium">{toEquipment?.equipment.name || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Type:</span>
                      <span className="font-medium capitalize">{selectedCable.type}</span>
                    </div>
                    {selectedCable.subtype && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Subtype:</span>
                        <span className="font-medium">{selectedCable.subtype}</span>
                      </div>
                    )}
                    {selectedCable.length && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Length:</span>
                        <span className="font-medium">{selectedCable.length}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Settings Configuration */}
      {isSettingsConfigOpen && (
        <div className="relative h-full overflow-hidden">
          {/* Main Settings View */}
          <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${showAccountSettings ? '-translate-x-full' : 'translate-x-0'} z-10`}>
            <div className="space-y-1 h-full overflow-y-auto custom-scrollbar pr-4" style={{ scrollbarGutter: 'stable' }}>
              {/* Account Section */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAccountSettings(true);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="w-full flex items-center space-x-2 mb-4 p-2 hover:bg-slate-800/50 rounded transition-colors cursor-pointer relative z-10"
                type="button"
              >
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 pointer-events-none">
                  {displayInitial}
            </div>
                <div className="flex-1 min-w-0 text-left pointer-events-none">
                  <div className="text-sm font-medium truncate">{displayName}</div>
                  <div className="text-xs text-gray-400 truncate">{accountEmail}</div>
            </div>
                <ChevronRight size={16} className="text-gray-400 flex-shrink-0 pointer-events-none" />
              </button>

              {/* General Section */}
              <div className="overflow-hidden">
                <button
                  onClick={() => toggleSection('general')}
                  className="w-full flex items-center justify-between py-2 px-1 hover:bg-slate-800/50 rounded transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Settings size={16} className="text-blue-400" />
                    <span className="font-medium text-sm">General</span>
                  </div>
                  {expandedSections.general ? (
                    <ChevronDown size={16} className="text-gray-400 transition-transform" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400 transition-transform" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedSections.general ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pt-1.5 pb-2 pl-6 space-y-2">
                {/* Autosave Settings */}
                <div className="pt-2">
                  <h4 className="font-medium mb-1.5 text-sm">Autosave</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium">Enable Autosave</label>
                      <button
                        onClick={() => updateAutosaveSettings({ enabled: !autosaveSettings.enabled })}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          autosaveSettings.enabled ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            autosaveSettings.enabled ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="text-xs text-gray-400 space-y-0.5">
                      {lastSaved && (
                        <div>Last saved: {new Date(lastSaved).toLocaleTimeString()}</div>
                      )}
                      {isAutoSaving && (
                        <div className="text-green-400 flex items-center space-x-1">
                          <span className="animate-pulse">â—</span>
                          <span>Saving...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Currency Settings */}
                <div className="pt-2">
                  <h4 className="font-medium mb-1.5 text-sm">Currency</h4>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium mb-1">Currency</label>
                      <select
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value as 'USD' | 'ZAR' | 'GBP')}
                        className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-md focus:outline-none focus:border-blue-500 text-sm text-white"
                      >
                        <option value="USD">US Dollar (USD)</option>
                        <option value="ZAR">Rand (ZAR)</option>
                        <option value="GBP">Pound (GBP)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Typography / UI font */}
                <div className="pt-2">
                  <h4 className="font-medium mb-1.5 text-sm">Typography</h4>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium mb-1">App font</label>
                      <select
                        value={appUIFont}
                        onChange={(e) => setAppUIFont(e.target.value as AppUIFont)}
                        className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-md focus:outline-none focus:border-blue-500 text-sm text-white"
                      >
                        <option value="inter">Inter (default)</option>
                        <option value="geist">Geist</option>
                        <option value="plus-jakarta">Plus Jakarta Sans</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Theme Settings */}
                <div className="pt-2">
                  <h4 className="font-medium mb-1.5 text-sm">Theme</h4>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium mb-1">App Theme</label>
                      <select
                        value={appTheme}
                        onChange={(e) => setAppTheme(e.target.value as 'default' | 'dark')}
                        className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-md focus:outline-none focus:border-blue-500 text-sm text-white"
                      >
                        <option value="default">Default</option>
                        <option value="dark">Dark</option>
                      </select>
                    </div>
                  </div>
                </div>

                  </div>
                </div>
              </div>

              {/* Designer Section */}
              <div className="overflow-hidden">
                <button
                  onClick={() => toggleSection('designer')}
                  className="w-full flex items-center justify-between py-2 px-1 hover:bg-slate-800/50 rounded transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Layout size={16} className="text-blue-400" />
                    <span className="font-medium text-sm">Designer</span>
                  </div>
                  {expandedSections.designer ? (
                    <ChevronDown size={16} className="text-gray-400 transition-transform" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400 transition-transform" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedSections.designer ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
              <div className="pt-1.5 pb-2 pl-6 space-y-2">
          {/* Visualization Settings */}
                <div className="pt-2">
                  <h4 className="font-medium mb-1.5 text-sm">Visualization</h4>
            <div className="space-y-2">
                    {/* Badge Position */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Badge Position</label>
                      <select
                        value={badgeAnchor}
                        onChange={(e) => setBadgeAnchor(e.target.value as any)}
                        className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-md focus:outline-none focus:border-blue-500 text-sm text-white"
                      >
                        <option value="top-left">Top Left (default)</option>
                        <option value="top-right">Top Right</option>
                        <option value="center-top">Center Top</option>
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                        <option value="bottom-left">Bottom Left</option>
                        <option value="bottom-right">Bottom Right</option>
                        <option value="center-bottom">Center Bottom</option>
                      </select>
                    </div>
                    {/* Canvas Background */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Canvas Background</label>
                      <select
                        value={canvasBackground}
                        onChange={(e) => setCanvasBackground(e.target.value as 'light' | 'dark')}
                        className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-md focus:outline-none focus:border-blue-500 text-sm text-white"
                      >
                        <option value="light">Light (default)</option>
                        <option value="dark">Dark grey</option>
                      </select>
                    </div>
                    {/* Panel Visualization */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Panel Visualization</label>
                      <select
                        value={panelVisualization}
                        onChange={(e) => setPanelVisualization(e.target.value as 'shaded' | 'minimalist')}
                        className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-md focus:outline-none focus:border-blue-500 text-sm text-white"
                      >
                        <option value="shaded">Shaded (Default)</option>
                        <option value="minimalist">Minimalist</option>
                      </select>
                    </div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">AP Range Visualization</label>
                <button
                  onClick={() => setShowAPRange(!showAPRange)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    showAPRange ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      showAPRange ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
                    {/* Always Show Expanded Badges */}
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium">Always Show Expanded Badges</label>
                      <button
                        onClick={() => setAlwaysShowExpandedEquipment(!alwaysShowExpandedEquipment)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          alwaysShowExpandedEquipment ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            alwaysShowExpandedEquipment ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                    {/* Show Manufacturer in Equipment List */}
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium">Show Manufacturer</label>
                      <button
                        onClick={() => setShowManufacturerInEquipmentList(!showManufacturerInEquipmentList)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          showManufacturerInEquipmentList ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            showManufacturerInEquipmentList ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                    {/* Do not expand badges when hovering */}
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium">Do not expand badges when hovering</label>
                      <button
                        onClick={() => setExpandBadgesOnHover(!expandBadgesOnHover)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          !expandBadgesOnHover ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            !expandBadgesOnHover ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                    {/* Port Display Mode */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Port Display</label>
                      <select
                        value={portsDisplayMode}
                        onChange={(e) => setPortsDisplayMode(e.target.value as 'none' | 'full')}
                        className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-md focus:outline-none focus:border-blue-500 text-sm text-white"
                      >
                        <option value="none">None</option>
                        <option value="full">Full (below badge)</option>
                      </select>
                    </div>
                    {/* Colourize Ports */}
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium">Colourize Ports</label>
                      <button
                        onClick={() => setColourizePorts(!colourizePorts)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          colourizePorts ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            colourizePorts ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                    {/* Advanced port table by default */}
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <label className="block text-sm font-medium">Advanced port table by default</label>
                        <p className="text-xs text-slate-400 mt-0.5">Force the floating panel into the port table. The header switch stays on and greyed out.</p>
                      </div>
                      <button
                        onClick={() => setEquipmentPanelAdvancedDefault(!equipmentPanelAdvancedDefault)}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                          equipmentPanelAdvancedDefault ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            equipmentPanelAdvancedDefault ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
            </div>
          </div>

          {/* Grid Settings */}
                <div className="pt-2 border-t border-slate-700">
                  <h4 className="font-medium mb-1.5 text-sm">Grid Settings</h4>
            <div className="space-y-2">
              {/* Snap to Grid Toggle */}
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Snap to Grid</label>
                <button
                  onClick={() => setSnapToGrid(!snapToGrid)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    snapToGrid ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      snapToGrid ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Show Grid</label>
                <button
                  onClick={() => setShowGrid(!showGrid)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    showGrid ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      showGrid ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              {snapToGrid && (
                <div>
                  <label className="block text-sm font-medium mb-1">Apply snap to</label>
                  <select
                    value={snapToGridTarget}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSnapToGridTarget(v === 'walls' || v === 'both' ? v : 'icons');
                    }}
                    className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-md focus:outline-none focus:border-blue-500 text-sm text-white"
                  >
                    <option value="icons">Icons</option>
                    <option value="walls">Walls</option>
                    <option value="both">Both</option>
                  </select>
                </div>
              )}

              {/* Grid Size Selection */}
              <div>
                <label className="block text-sm font-medium mb-1">Grid Size</label>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setGridSize(gridSize - 1)}
                    disabled={gridSize <= 1}
                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-slate-600 bg-slate-700 text-white hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Decrease grid size"
                  >
                    <Minus size={14} />
                  </button>
                  <div className="relative flex-1">
                    <input
                      type="number"
                      min={1}
                      max={200}
                      step={1}
                      value={gridSize}
                      onChange={(e) => setGridSize(Number(e.target.value))}
                      className="w-full rounded-md border border-slate-600 bg-slate-700 py-1.5 pl-2 pr-8 text-sm text-white [appearance:textfield] focus:border-blue-500 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">px</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGridSize(gridSize + 1)}
                    disabled={gridSize >= 200}
                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-slate-600 bg-slate-700 text-white hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Increase grid size"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>

            </div>
          </div>

          {/* Wall Drag */}
                <div className="pt-2 border-t border-slate-700">
                  <h4 className="font-medium mb-1.5 text-sm">Wall Drag</h4>
                  <div>
                    <label className="block text-sm font-medium mb-1">Drag Mode</label>
                    <select
                      value={wallDragMode}
                      onChange={(e) => setWallDragMode(e.target.value === 'extend' ? 'extend' : 'default')}
                      className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-md focus:outline-none focus:border-blue-500 text-sm text-white"
                    >
                      <option value="default">Default — keep squared</option>
                      <option value="extend">Extend — selected wall only</option>
                    </select>
                  </div>
                </div>

          {/* Canvas Legend */}
                <div className="pt-2 border-t border-slate-700">
                  <h4 className="font-medium mb-1.5 text-sm">Canvas Legend</h4>
            <div className="space-y-2">
              {/* Show Legend Toggle */}
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Show Legend</label>
                <button
                  onClick={() => setShowLegend(!showLegend)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    showLegend ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      showLegend ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                    </div>
                  {/* Minimap Toggle */}
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium">Show Minimap</label>
                    <button
                      onClick={() => setShowMinimap(!showMinimap)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        showMinimap ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          showMinimap ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                  {/* Design Info Toggle */}
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium">Show Design Info</label>
                    <button
                      onClick={() => setShowDesignInfoOverlay(!showDesignInfoOverlay)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        showDesignInfoOverlay ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          showDesignInfoOverlay ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

              {/* Sites Section */}
              <div className="overflow-hidden">
                <button
                  onClick={() => toggleSection('sites')}
                  className="w-full flex items-center justify-between py-2 px-1 hover:bg-slate-800/50 rounded transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Radio size={16} className="text-blue-400" />
                    <span className="font-medium text-sm">Sites</span>
                  </div>
                  {expandedSections.sites ? (
                    <ChevronDown size={16} className="text-gray-400 transition-transform" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400 transition-transform" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedSections.sites ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
              <div className="pt-1.5 pb-2 pl-6 space-y-2">
                <div className="pt-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Tower Labels</label>
                <button
                      onClick={() => setShowTowerLabels(!showTowerLabels)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        showTowerLabels ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          showTowerLabels ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                  </div>
                </div>
                  </div>
                </div>
              </div>

              {/* Floorplan Section */}
              <div className="overflow-hidden">
                <button
                  onClick={() => toggleSection('floorplan')}
                  className="w-full flex items-center justify-between py-2 px-1 hover:bg-slate-800/50 rounded transition-colors"
                  >
                  <div className="flex items-center gap-2">
                    <Ruler size={16} className="text-blue-400" />
                    <span className="font-medium text-sm">Floorplan</span>
                </div>
                  {expandedSections.floorplan ? (
                    <ChevronDown size={16} className="text-gray-400 transition-transform" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400 transition-transform" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedSections.floorplan ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
              <div className="pt-1.5 pb-2 pl-6 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-300">Units</span>
                  <select
                    value={floorPlan?.units || floorPlanUnits}
                    onChange={(e) => setFloorPlanUnits(e.target.value as 'metric' | 'imperial')}
                    className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                  >
                    <option value="metric">Meters (m)</option>
                    <option value="imperial">Feet (ft)</option>
                  </select>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCanvasMode(canvasMode === 'wall' ? 'place' : 'wall')}
                    className="px-2 py-1 text-[11px] rounded bg-blue-700/60 hover:bg-blue-600 text-white"
                  >
                    {canvasMode === 'wall' ? 'Exit wall mode' : 'Draw walls'}
                  </button>
                  {referenceImages.length > 0 && (
                    <button
                      type="button"
                      onClick={() => clearFloorPlanReferenceImages()}
                      className="px-2 py-1 text-[11px] rounded bg-slate-700 hover:bg-slate-600 text-white"
                    >
                      Clear all references
                    </button>
                  )}
                </div>


                <div className="text-[11px] text-slate-500">
                  Walls: {floorPlan?.walls.length ?? 0} · Rooms: {floorPlan?.rooms.length ?? 0} · Doors: {floorPlan?.openings.length ?? 0}
                  {referenceImages.length > 0 ? ` · Refs: ${referenceImages.length}` : ''}
                  <span className="block mt-1 text-slate-600">Use the Browser panel to show/hide items.</span>
                </div>
                  </div>
                </div>
              </div>

              {/* Reports Section */}
              <div className="overflow-hidden">
                <button
                  onClick={() => toggleSection('reports')}
                  className="w-full flex items-center justify-between py-2 px-1 hover:bg-slate-800/50 rounded transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-blue-400" />
                    <span className="font-medium text-sm">Reports</span>
                  </div>
                  {expandedSections.reports ? (
                    <ChevronDown size={16} className="text-gray-400 transition-transform" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400 transition-transform" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedSections.reports ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pt-1.5 pb-2 pl-6 space-y-2">
                    <div className="pt-2 text-xs text-gray-400">
                      Settings for Reports will be added here.
                  </div>
                  </div>
                </div>
              </div>

              {/* Proposal Section */}
              <div className="overflow-hidden">
                <button
                  onClick={() => toggleSection('proposal')}
                  className="w-full flex items-center justify-between py-2 px-1 hover:bg-slate-800/50 rounded transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FileCheck size={16} className="text-blue-400" />
                    <span className="font-medium text-sm">Proposal</span>
                  </div>
                  {expandedSections.proposal ? (
                    <ChevronDown size={16} className="text-gray-400 transition-transform" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400 transition-transform" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedSections.proposal ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pt-1.5 pb-2 pl-6 space-y-2">
                    <div className="pt-2 text-xs text-gray-400">
                      Settings for Proposal will be added here.
                    </div>
              </div>
            </div>
          </div>

              {/* Analytics Section */}
              <div className="overflow-hidden">
                <button
                  onClick={() => toggleSection('analytics')}
                  className="w-full flex items-center justify-between py-2 px-1 hover:bg-slate-800/50 rounded transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <BarChart3 size={16} className="text-blue-400" />
                    <span className="font-medium text-sm">Analytics</span>
                </div>
                  {expandedSections.analytics ? (
                    <ChevronDown size={16} className="text-gray-400 transition-transform" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400 transition-transform" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedSections.analytics ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pt-1.5 pb-2 pl-6 space-y-2">
                    <div className="pt-2 text-xs text-gray-400">
                      Settings for Analytics will be added here.
                </div>
                </div>
                </div>
                </div>

              {/* Beta Features Section */}
              <div className="overflow-hidden">
                <button
                  onClick={() => toggleSection('betaFeatures')}
                  className="w-full flex items-center justify-between py-2 px-1 hover:bg-slate-800/50 rounded transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-blue-400" />
                    <span className="font-medium text-sm">Beta Features</span>
                </div>
                  {expandedSections.betaFeatures ? (
                    <ChevronDown size={16} className="text-gray-400 transition-transform" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400 transition-transform" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedSections.betaFeatures ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
              <div className="pt-1.5 pb-2 pl-6 space-y-2">
                <div className="pt-2">
                  <div className="bg-gradient-to-br from-blue-900/50 to-purple-900/50 rounded-lg p-3 border border-blue-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-medium flex items-center gap-2 text-sm">
                          <span className="inline-block px-2 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded">BETA</span>
                          New Features
                        </h4>
              </div>
            </div>

                    <div className="space-y-2">
                      <div className="bg-slate-800/50 rounded-lg p-2.5 border border-slate-700">
                        <label className="block text-sm font-medium mb-1">Canvas collab</label>
                        <div className="space-y-1.5">
                          {(
                            [
                              { id: 'default', label: 'Default (live)' },
                              { id: 'commitOnDrop', label: 'A — Commit on drop' },
                              { id: 'livePreview', label: 'B — Live preview' },
                            ] as const
                          ).map((option) => (
                            <label key={option.id} className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="canvasCollabMode"
                                className="mt-0.5"
                                checked={canvasCollabMode === option.id}
                                onChange={() => setCanvasCollabMode(option.id)}
                              />
                              <span className="block text-xs font-medium">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Advanced Cable Run */}
                      <div className="bg-slate-800/50 rounded-lg p-2.5 border border-slate-700">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex-1 min-w-0">
                            <label className="block text-sm font-medium flex items-center gap-2">
                              Advanced Cable Run
                              {featureFlags.showBetaBadges && (
                                <span className="px-1.5 py-0.5 bg-orange-600 text-white text-[9px] font-bold rounded">BETA</span>
                              )}
                            </label>
                          </div>
                          <button
                            onClick={() => updateFeatureFlags({ enableAdvancedCableRun: !featureFlags.enableAdvancedCableRun })}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                              featureFlags.enableAdvancedCableRun ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                            }`}
                          >
                            <span
                              className={`inline-block h-3 w-3 rounded-full bg-white transition-transform ${
                                featureFlags.enableAdvancedCableRun ? 'translate-x-5' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Custom Templates */}
                      <div className="bg-slate-800/50 rounded-lg p-2.5 border border-slate-700 opacity-60">
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="block text-sm font-medium text-gray-400">Custom Templates</label>
                          <button
                            disabled
                            className="relative inline-flex h-5 w-9 items-center rounded-full bg-gray-700 cursor-not-allowed"
                          >
                            <span className="inline-block h-3.5 w-3.5 transform rounded-full bg-gray-500 translate-x-0.5" />
                          </button>
                        </div>
                      </div>

                      {/* Design Info Overlay */}
                      <div className="bg-slate-800/50 rounded-lg p-2.5 border border-slate-700">
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="block text-xs font-medium flex items-center gap-2">
                              Design Info Overlay (Top-Right)
                              {featureFlags.showBetaBadges && (
                                <span className="px-1.5 py-0.5 bg-orange-600 text-white text-[9px] font-bold rounded">BETA</span>
                              )}
                            </label>
                          <button
                            onClick={() => setShowDesignInfoOverlay(!showDesignInfoOverlay)}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                              showDesignInfoOverlay ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                            }`}
                          >
                            <span
                              className={`inline-block h-3 w-3 rounded-full bg-white transition-transform ${
                                showDesignInfoOverlay ? 'translate-x-5' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Show Beta Badges */}
                      <div className="flex items-center justify-between pt-1.5 border-t border-slate-700">
                        <label className="block text-xs font-medium">Show Beta Badges</label>
                        <button
                          onClick={() => updateFeatureFlags({ showBetaBadges: !featureFlags.showBetaBadges })}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            featureFlags.showBetaBadges ? 'bg-blue-600 settings-toggle-on' : 'bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              featureFlags.showBetaBadges ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Reset Button */}
                    <div className="mt-3 pt-2 border-t border-slate-700">
                      <button
                        onClick={() => {
                          if (window.confirm('Reset all feature flags to default settings? This will disable all beta features.')) {
                            resetFeatureFlags();
                          }
                        }}
                        className="w-full px-2.5 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded-md transition-colors"
                      >
                        Reset to Defaults
                      </button>
                    </div>
                  </div>
                </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Settings View */}
          <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${showAccountSettings ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="space-y-1 h-full overflow-y-auto custom-scrollbar pr-4" style={{ scrollbarGutter: 'stable' }}>
              {/* Back Button */}
              <button
                onClick={() => setShowAccountSettings(false)}
                className="w-full flex items-center space-x-2 mb-4 p-2 hover:bg-slate-800/50 rounded transition-colors"
              >
                <ChevronRight size={16} className="text-gray-400 rotate-180 flex-shrink-0" />
                <div className="flex items-center space-x-2 flex-1">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                    {displayInitial}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-medium truncate">{displayName}</div>
                    <div className="text-xs text-gray-400 truncate">{accountEmail}</div>
                  </div>
                </div>
              </button>

              {/* Account Settings Content */}
              <div className="space-y-4">
                <div className="bg-slate-800 rounded-lg p-4">
                  <h3 className="font-medium mb-3 text-sm">Account Settings</h3>
                  <p className="text-xs text-gray-400">Account settings will be added here.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default RightSidebar;
